import { Barcode } from '@phosphor-icons/react';
import { Placeholder } from 'semantic-ui-react';
import styled from 'styled-components';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppTable } from '../../../../components';
import { HeaderText, ShadowBox } from '../../../../elements';
import { PackagingListRow } from '../../../../page-components';
import { theme, ToastMessage } from '../../../../utility';
import { PackagingHook } from '../../../../hooks/packing';
import { useBarcodeScanner } from '../../../../hooks';
import { PackagingService } from '../../../../utility/services/packaging';
import { useGlobalContext } from '../../../../contexts';
import { useAuth } from '../../../../contexts/auth';
import {
  createScannerDiagnostics,
  redactBarcode,
  shouldEnableScannerDiagnostics,
} from '../../../../hooks/barcode-scanner';
import { usePackingTabExtraContent } from '../../../../contexts/packing-tab-extra';
import { PackingTabToolbar } from '../../../../page-components/packaging/packing-tab-toolbar';

const StyledPlacholder = styled(Placeholder)`
  margin-top: -6px;
`;

export function PickListTab({ warehouseId }) {
  const { stationOptions, stationId, changeStation, currentWarehouse, currentStation } =
    useGlobalContext();
  const { currentOrg, user } = useAuth();

  const {
    filter: tableFilter,
    filterChanged,
    loading: orderLoading,
    data: orderData,
    totalData,
    totalPages,
    pageChanged,
  } = PackagingHook(warehouseId);

  const navigate = useNavigate();

  const [barcodeValue, setBarcodeValue] = useState('');
  const [processing, setProcessing] = useState(false);

  const resetBarcode = () => {
    setBarcodeValue('');
  };

  const scannerDiagnostics = useMemo(
    () =>
      createScannerDiagnostics({
        enabled: shouldEnableScannerDiagnostics({ currentOrg, user }),
        context: {
          ticket: 'POR-464',
          screen: 'packing-list',
          warehouseId,
          warehouseName: currentWarehouse?.name,
          stationId: currentStation?.stationId,
          stationName: currentStation?.stationName,
          organizationId: currentOrg?._id || user?.organizationId,
          organizationName: currentOrg?.name,
          userId: user?._id,
        },
        upload: payload => PackagingService.uploadScannerDiagnostics(payload),
      }),
    [currentOrg, currentStation, currentWarehouse, user, warehouseId],
  );

  useBarcodeScanner(
    scanValue => {
      setBarcodeValue(scanValue);
    },
    {
      disabled: processing || orderLoading,
      diagnostics: scannerDiagnostics,
    },
  );

  const checkData = async code => {
    setProcessing(true);
    scannerDiagnostics.record('container-scan-dispatch', {
      barcode: redactBarcode(code),
      warehouseId,
    });

    try {
      await PackagingService.getPackingContainerDetails({ code, warehouseId });
      scannerDiagnostics.record('container-scan-success', {
        barcode: redactBarcode(code),
      });
      scannerDiagnostics.flush('container-opened');
      navigate(
        `/app/fulfill/packing/details/${encodeURIComponent(code)}/?warehouseId=${warehouseId}`,
      );
    } catch (error) {
      scannerDiagnostics.record('container-scan-error', {
        status: error?.status,
        code: error?.code,
      });
      scannerDiagnostics.flush('container-scan-error');
      if (error?.status === 404) {
        ToastMessage.error(
          'Container with such code not found. Kindly rescan the container.',
        );
      } else {
        ToastMessage.apiError(error);
      }
    } finally {
      resetBarcode();
      setProcessing(false);
    }
  };

  useEffect(() => {
    if (barcodeValue && !processing) {
      checkData(barcodeValue);
    }
  }, [barcodeValue]);

  usePackingTabExtraContent(
    () => (
      <PackingTabToolbar
        stationOptions={stationOptions}
        stationId={stationId}
        onStationChange={changeStation}
      />
    ),
    [stationOptions, stationId, changeStation],
  );

  const orderColumns = [
    {
      text: 'Wave ID',
      key: 'wave_id',
      textAlign: 'left',
      width: 2,
    },
    {
      text: 'Status',
      key: 'status',
      textAlign: 'left',
      width: 2,
    },
    {
      text: 'Last Updated',
      key: 'created',
      width: 2,
      textAlign: 'left',
    },
    {
      text: 'Container',
      key: 'container',
      width: 2,
      textAlign: 'left',
    },
    {
      text: 'Contents',
      key: 'contents',
      width: 2,
      textAlign: 'left',
    },
    {
      text: '',
      key: 'action',
      width: 2,
      textAlign: 'left',
    },
  ];

  return (
    <div>
      <ShadowBox className="d-flex align-item-center mt-2">
        {processing ? (
          <StyledPlacholder fluid className="w-100">
            <Placeholder.Line />
            <Placeholder.Line />
          </StyledPlacholder>
        ) : (
          <>
            <Barcode color={theme.colors.grey} size={32} />
            <HeaderText className="ml-2 mt-1">Scan a Container to Get Started</HeaderText>
          </>
        )}
      </ShadowBox>
      <AppTable
        columns={orderColumns}
        loading={orderLoading}
        RowComponent={<PackagingListRow />}
        data={orderData}
        totalData={totalData}
        totalPages={totalPages}
        pageChanged={pageChanged}
        currentPage={tableFilter.page}
        pageSizeChanged={size => filterChanged('limit', size)}
        pageSize={tableFilter.limit}
      />
    </div>
  );
}
